import { isApiError, type ApiErrorCode } from '@azko/shared';

/**
 * Cliente HTTP unico do front.
 *
 * Em dev o Vite faz proxy de /api para a API, entao nao ha CORS nem URL
 * diferente por ambiente. `credentials: 'include'` leva o cookie de sessao.
 */
const BASE_URL = import.meta.env.VITE_API_URL ?? '/api';

const CONDOMINIUM_STORAGE_KEY = 'azko:condominium';

/** Condominio ativo — vai em toda request como header X-Condominium. */
export function getActiveCondominium(): string | null {
  return localStorage.getItem(CONDOMINIUM_STORAGE_KEY);
}

export function setActiveCondominium(publicId: string | null): void {
  if (publicId) localStorage.setItem(CONDOMINIUM_STORAGE_KEY, publicId);
  else localStorage.removeItem(CONDOMINIUM_STORAGE_KEY);
}

export class ApiRequestError extends Error {
  readonly status: number;
  readonly code: ApiErrorCode;
  readonly fields: Record<string, string>;
  /** Chave do entitlement que faltou — usada para abrir a tela de upgrade. */
  readonly entitlement: string | null;

  constructor(
    status: number,
    code: ApiErrorCode,
    message: string,
    fields: Record<string, string> = {},
    entitlement: string | null = null,
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.status = status;
    this.code = code;
    this.fields = fields;
    this.entitlement = entitlement;
  }

  /** True quando a acao falhou por causa do plano, nao por erro do usuario. */
  get isPlanBlock(): boolean {
    return (
      this.code === 'plan_limit_reached' ||
      this.code === 'module_not_available' ||
      this.code === 'subscription_inactive'
    );
  }
}

export async function apiFetch<T>(path: string, init: RequestInit = {}): Promise<T> {
  const condominium = getActiveCondominium();

  const response = await fetch(`${BASE_URL}${path}`, {
    ...init,
    // Dados de sessao, moradores e encomendas nunca devem cair no cache HTTP
    // do navegador. O service worker tambem nao intercepta nenhuma API.
    cache: 'no-store',
    credentials: 'include',
    headers: {
      ...(init.body ? { 'Content-Type': 'application/json' } : {}),
      ...(condominium ? { 'X-Condominium': condominium } : {}),
      ...init.headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    if (isApiError(payload)) {
      throw new ApiRequestError(
        response.status,
        payload.error.code,
        payload.error.message,
        payload.error.fields ?? {},
        payload.error.entitlement ?? null,
      );
    }
    throw new ApiRequestError(response.status, 'internal_error', 'Falha de comunicacao com o servidor.');
  }

  return payload as T;
}

/**
 * Busca um binario da API — hoje a assinatura de retirada e a foto do pacote.
 *
 * Existe porque `<img src="/api/packages/.../signature">` NAO funciona: o
 * navegador manda o cookie de sessao, mas nao manda o header `X-Condominium`,
 * e sem ele a API nao sabe de qual condominio e a request e recusa. A imagem
 * chega como blob e vira uma object URL local.
 *
 * Quem chama e dono da URL devolvida e precisa chamar `URL.revokeObjectURL`
 * ao desmontar; sem isso cada comprovante aberto segura os bytes na memoria da
 * aba ate o refresh.
 */
export async function apiBlobUrl(path: string): Promise<string> {
  const condominium = getActiveCondominium();

  const response = await fetch(`${BASE_URL}${path}`, {
    // Fotos e assinaturas sao ainda mais sensiveis que o JSON da API.
    cache: 'no-store',
    credentials: 'include',
    headers: condominium ? { 'X-Condominium': condominium } : {},
  });

  if (!response.ok) {
    // O corpo de erro da API e JSON mesmo numa rota que responde imagem.
    const payload: unknown = await response.json().catch(() => null);
    if (isApiError(payload)) {
      throw new ApiRequestError(
        response.status,
        payload.error.code,
        payload.error.message,
        payload.error.fields ?? {},
        payload.error.entitlement ?? null,
      );
    }
    throw new ApiRequestError(response.status, 'internal_error', 'Falha ao carregar a imagem.');
  }

  return URL.createObjectURL(await response.blob());
}

export const api = {
  get: <T>(path: string) => apiFetch<T>(path),
  blobUrl: apiBlobUrl,
  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'POST', body: body === undefined ? undefined : JSON.stringify(body) }),
  patch: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body: body === undefined ? undefined : JSON.stringify(body) }),
  // PUT e o verbo de quem SUBSTITUI o conjunto inteiro — os vinculos do
  // morador com as unidades. PATCH ficou para alteracao parcial.
  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body: body === undefined ? undefined : JSON.stringify(body) }),
  delete: <T>(path: string) => apiFetch<T>(path, { method: 'DELETE' }),
};
